import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ministeriosService,
  type DadosFuncao,
} from "@/core/services/ministeriosService";
import { useAuth } from "@/core/hooks/useAuth";
import type { PessoaMinisterioStatus } from "@/shared/types/database.types";

const QUERY_KEY_MINISTERIOS = ["ministerios"];
const detalheKey = (id: string) => ["ministerio", id];

/**
 * Invalidação em bloco: mudanças em funções/vínculos afetam a lista e o
 * detalhe — melhor invalidar de sobra do que exibir dado velho.
 */
function useInvalidarMinisterios() {
  const queryClient = useQueryClient();
  return (ministerioId?: string) => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY_MINISTERIOS });
    if (ministerioId) queryClient.invalidateQueries({ queryKey: detalheKey(ministerioId) });
  };
}

export function useMinisterios() {
  return useQuery({ queryKey: QUERY_KEY_MINISTERIOS, queryFn: ministeriosService.listar });
}

/** Ministérios ativos que a pessoa logada LIDERA (card "Meu ministério"). */
export function useMinisteriosQueLidero() {
  const { pessoa } = useAuth();
  return useQuery({
    queryKey: ["ministerios-que-lidero", pessoa?.id ?? ""],
    queryFn: () => ministeriosService.meusMinisteriosLiderados(pessoa!.id),
    enabled: !!pessoa,
  });
}

/**
 * Ministérios ativos em que a pessoa logada tem função de Gestor de elo (cartão "Elos que você
 * gerencia" do Início e lições da Ajuda). Só orienta; o portão real é o banco.
 */
export function useMinisteriosQueGerencio() {
  const { pessoa } = useAuth();
  return useQuery({
    queryKey: ["ministerios-que-gerencio", pessoa?.id ?? ""],
    queryFn: () => ministeriosService.meusMinisteriosComoGestor(pessoa!.id),
    enabled: !!pessoa,
  });
}

export function useMinisterioDetalhe(id: string | undefined) {
  return useQuery({
    queryKey: detalheKey(id ?? ""),
    queryFn: () => ministeriosService.detalhe(id as string),
    enabled: !!id,
  });
}

export function useCriarMinisterio() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({ nome, descricao }: { nome: string; descricao: string | null }) =>
      ministeriosService.criar(nome, descricao),
    onSuccess: () => invalidar(),
  });
}

export function useAtualizarMinisterio() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({ id, nome, descricao }: { id: string; nome: string; descricao: string | null }) =>
      ministeriosService.atualizar(id, { nome, descricao }),
    onSuccess: (_d, v) => invalidar(v.id),
  });
}

export function useExcluirMinisterio() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: (id: string) => ministeriosService.excluir(id),
    onSuccess: () => invalidar(),
  });
}

export function useDefinirMinisterioAtivo() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({ id, ativo }: { id: string; ativo: boolean }) =>
      ministeriosService.definirAtivo(id, ativo),
    onSuccess: (_d, v) => invalidar(v.id),
  });
}

export function useCriarFuncao() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({ ministerioId, dados }: { ministerioId: string; dados: DadosFuncao }) =>
      ministeriosService.criarFuncao(ministerioId, dados),
    onSuccess: (_d, v) => invalidar(v.ministerioId),
  });
}

export function useAtualizarFuncao() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({
      ministerioId: _ministerioId,
      funcaoId,
      dados,
    }: {
      ministerioId: string;
      funcaoId: string;
      dados: DadosFuncao;
    }) => ministeriosService.atualizarFuncao(funcaoId, dados),
    onSuccess: (_d, v) => invalidar(v.ministerioId),
  });
}

export function useExcluirFuncao() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({ ministerioId: _m, funcaoId }: { ministerioId: string; funcaoId: string }) =>
      ministeriosService.excluirFuncao(funcaoId),
    onSuccess: (_d, v) => invalidar(v.ministerioId),
  });
}

export function useAdicionarIntegrante() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({
      pessoaId,
      ministerioId,
      funcaoId,
      status,
    }: {
      pessoaId: string;
      ministerioId: string;
      funcaoId: string;
      status: Extract<PessoaMinisterioStatus, "ativo" | "convidado">;
    }) => ministeriosService.adicionarIntegrante(pessoaId, ministerioId, funcaoId, status),
    onSuccess: (_d, v) => invalidar(v.ministerioId),
  });
}

export function useRemoverIntegrante() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({ ministerioId: _m, vinculoId }: { ministerioId: string; vinculoId: string }) =>
      ministeriosService.removerIntegrante(vinculoId),
    onSuccess: (_d, v) => invalidar(v.ministerioId),
  });
}

/** A39: remove TODAS as funções da pessoa no ministério de uma vez (desvincula). */
export function useRemoverTodosVinculos() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({ ministerioId, pessoaId }: { ministerioId: string; pessoaId: string }) =>
      ministeriosService.removerTodosVinculos(ministerioId, pessoaId),
    onSuccess: (_d, v) => invalidar(v.ministerioId),
  });
}

export function useDefinirLider() {
  const invalidar = useInvalidarMinisterios();
  return useMutation({
    mutationFn: ({ ministerioId, pessoaId }: { ministerioId: string; pessoaId: string }) =>
      ministeriosService.definirLider(ministerioId, pessoaId),
    onSuccess: (_d, v) => invalidar(v.ministerioId),
  });
}
